import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import {
  runThemeTransition,
  type ThemeOrigin,
} from "#shared/lib/theme-transition.ts";

type Theme = "light" | "dark";
type ThemePreference = Theme | "system";

const THEME_CHANGE_EVENT = "gain-theme-change";

function getSystemTheme(): Theme {
  if (typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
  root.style.removeProperty("background-color");
}

function isApplied(theme: Theme): boolean {
  return (
    document.documentElement.classList.contains("dark") === (theme === "dark")
  );
}

function readPreference(): ThemePreference {
  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

function resolveTheme(): Theme {
  const preference = readPreference();
  return preference === "system" ? getSystemTheme() : preference;
}

function publishTheme(): void {
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

let systemListeners = 0;
let detachSystem: (() => void) | undefined;

function subscribeSystem(): () => void {
  systemListeners += 1;
  if (systemListeners === 1 && typeof window.matchMedia === "function") {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (readPreference() !== "system") return;
      const next = getSystemTheme();
      const update = () => {
        applyTheme(next);
        publishTheme();
      };
      if (isApplied(next)) update();
      else runThemeTransition(update);
    };
    media.addEventListener("change", onChange);
    detachSystem = () => media.removeEventListener("change", onChange);
  }
  return () => {
    systemListeners -= 1;
    if (systemListeners === 0) {
      detachSystem?.();
      detachSystem = undefined;
    }
  };
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => resolveTheme());
  const [preference, setPreference] = useState<ThemePreference>(() =>
    readPreference(),
  );

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => subscribeSystem(), []);

  useEffect(() => {
    const syncTheme = () => {
      const next = resolveTheme();
      applyTheme(next);
      flushSync(() => {
        setTheme(next);
        setPreference(readPreference());
      });
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === "theme" || event.key === null) syncTheme();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(THEME_CHANGE_EVENT, syncTheme);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(THEME_CHANGE_EVENT, syncTheme);
    };
  }, []);

  const commit = (
    next: Theme,
    nextPreference: ThemePreference,
    origin?: ThemeOrigin,
  ) => {
    const update = () => {
      localStorage.setItem("theme", nextPreference);
      applyTheme(next);
      publishTheme();
    };
    if (isApplied(next)) update();
    else runThemeTransition(update, origin);
  };

  const select = (next: Theme, origin?: ThemeOrigin) => {
    commit(next, next, origin);
  };

  const toggle = (origin?: ThemeOrigin) => {
    select(theme === "light" ? "dark" : "light", origin);
  };

  const resetToSystem = (origin?: ThemeOrigin) => {
    commit(getSystemTheme(), "system", origin);
  };

  return { theme, preference, toggle, select, resetToSystem };
}
